// @flow strict

import queryString from 'query-string';
import React, { useMemo } from 'react';
import { Button, FormGridCell, TextInput, Message } from '@ansarada/ace-react';
import { useFormik } from 'formik';
import axios from 'axios';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import cx from 'classnames';
// eslint-disable-next-line css-modules/no-unused-class
import styles from './styles.scss';
import logo from '../../../../static/images/ansarada-logo-symbol.svg';
import googleLogo from '../../../../static/images/google_logo.svg';
import linkedinLogo from '../../../../static/images/linkedin_logo.svg';
import microsoftLogo from '../../../../static/images/microsoft_logo.png';
import LoadingPlaceholder from '../LoadingPlaceholder';
import SignUpLayout from '../SignUpLayout';
import { saveSubscription, getSocialSignupLink, createSubsriptionAndProvision } from './helper';
import { getMsStartupQueryParams } from '../../helpers';
import { setUpflowyDataStorage } from '../../../FreeTrialOnboarding/helpers';
import type { CustomerInfo } from '../../types';
import type { UpflowyStorageData } from '../../../FreeTrialOnboarding/types';
import { SignUpSchema } from '../../../../core/SignUp';
import { deleteCookie, getCookie } from '../../../../core/cookie';
import { Link } from '../../../../internal/Button';
import type { AnalyticsType } from '../../../../internal/types';
import { apiUrl } from '../../../../core/api/apiConfig';
import { useClickOnEnter } from '../../../../hooks';

type PropsType = {
  onNext: CustomerInfo => void,
  onClientExists: CustomerInfo => void,
  restrictedEmails: Array<string>,
  restrictedEmailMessage: string,
  sendGridApiKey: string,
  useSimplifiedMobileSignUp: boolean,
  enabledMsStartupSignup: boolean,
  enabledStartupConferenceSignup: boolean,
  enabledUpflowy: boolean,
  signupUserId: string,
  enabledSmsIntegration: boolean,
};

// const apiKey = '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';

const EMPTY_HINT = '\u00A0';

const getSubmitButton = (enabledMsStartupSignup, enabledStartupConferenceSignup) => {
  if (enabledMsStartupSignup) {
    return 'Start your free 12 months access';
  }

  if (enabledStartupConferenceSignup) {
    return 'Start your free 6 months access';
  }

  return 'Start your free 14 day trial';
};

const SignUpFormV2 = ({
  onNext,
  onClientExists,
  restrictedEmails,
  restrictedEmailMessage,
  sendGridApiKey,
  useSimplifiedMobileSignUp,
  enabledMsStartupSignup,
  enabledStartupConferenceSignup,
  enabledUpflowy,
  signupUserId,
  enabledSmsIntegration,
}: PropsType) => {
  const firstInputRef: { current: null | HTMLElement } = React.useRef(null);
  const [recaptchaFailed, setRecaptchaFailed] = React.useState(false);
  const [isLoading, setLoading] = React.useState(true);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const submitButtonRef = React.useRef<?HTMLButtonElement>();

  const validationSchema = useMemo(
    () =>
      SignUpSchema({
        restrictedEmails,
        restrictedEmailMessage,
        sendGridApiKey,
        isEmailOnly: true,
      }),
    [restrictedEmails, restrictedEmailMessage, sendGridApiKey],
  );

  // Detect device see https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent#mobile_tablet_or_desktop
  const isMobileDevice = /Mobi/i.test(window.navigator.userAgent);
  const isUsingSimplified = useSimplifiedMobileSignUp && isMobileDevice;
  const isMsStartupOrStartupConference = enabledMsStartupSignup || enabledStartupConferenceSignup;
  const isSmsIntegration = enabledSmsIntegration && enabledMsStartupSignup;

  const onSubmit = async email => {
    const saveCustomer = async () => {
      const recaptchaAction = isSmsIntegration
        ? 'MsStartupCreateSubscriptionAndProvision'
        : 'FreeTrialForm';
      const recaptchaToken = await executeRecaptcha(recaptchaAction);
      const response = isSmsIntegration
        ? await createSubsriptionAndProvision({
            signupUserId,
            email,
            recaptchaAction,
            recaptchaToken,
          })
        : await axios.post(`${window.location.origin}/free-trial/save-customer`, {
            dto: {
              Email: email,
              RecaptchaToken: recaptchaToken,
              RecaptchaAction: recaptchaAction,
            },
          });

      const customerData = {
        firstName: email,
        email,
        customerId: '',
      };

      if (response.data.Status === 0) {
        customerData.customerId = response.data.CustomerId;
        if (enabledUpflowy && !isMsStartupOrStartupConference) {
          analytics.identify({ signup_user_id: signupUserId });

          const data: UpflowyStorageData = {
            userEmail: email,
            customerId: response.data.CustomerId,
            signupUserId,
          };
          setUpflowyDataStorage(data);

          const encodedSignupUserId = encodeURIComponent(btoa(signupUserId));
          window.location.href = new URL(
            `${
              apiUrl.ansaradadotcomBaseUrl
            }/free-trial-onboarding?id=${encodedSignupUserId}&plg_experimental=1`,
          );
          return null;
        }

        if ((isUsingSimplified || isMsStartupOrStartupConference) && !isSmsIntegration) {
          return saveSubscription({
            customerId: customerData.customerId,
            email,
            executeRecaptcha,
            setRecaptchaFailed,
            enabledMsStartupSignup,
            enabledStartupConferenceSignup,
          });
        }

        if (isSmsIntegration) {
          const encodedEmail = encodeURIComponent(btoa(email));
          window.location.href = `/free-trial-thankyou?e=${encodedEmail}${getMsStartupQueryParams(
            enabledMsStartupSignup,
            enabledStartupConferenceSignup,
          )}`;
        } else {
          onNext(customerData);
        }
      } else if (response.data.Status === 1) {
        onClientExists(customerData);
      } else if (response.data.Status === 3) {
        setRecaptchaFailed(true);
        setLoading(false);
      } else {
        window.location.href = '/500';
      }
      return null;
    };

    const analyticsEvent: AnalyticsType = {
      event: 'f_website_start-14-day-trial.clicked',
      properties: {
        signup_email: email,
      },
    };

    analytics.track(analyticsEvent.event, analyticsEvent.properties);

    setRecaptchaFailed(false);

    try {
      await saveCustomer();
    } catch {
      window.location.href = '/500';
    }
  };

  const {
    errors,
    touched,
    values: { Email },
    setFieldValue,
    setFieldTouched,
    isSubmitting,
    handleSubmit,
  } = useFormik({
    initialValues: {
      Email: '',
      RecaptchaToken: '',
    },
    validateOnChange: false,
    validationSchema,
    onSubmit: async (values, actions) => {
      const email = values.Email.trim();
      await onSubmit(email);
      actions.setSubmitting(false);
    },
  });

  const setFieldValueByName = name => val => setFieldValue(name, val);
  const setFieldTouchedByName = name => () => setFieldTouched(name, true);
  const fieldStatusByName = name => (touched[name] && errors[name] ? 'Bad' : 'Good');

  useClickOnEnter(submitButtonRef);

  React.useEffect(() => {
    setLoading(false);
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
    let parsedEmail = '';

    const cookieName = 'freeTrialEmail';
    const cookie = getCookie(cookieName);
    if (cookie) {
      parsedEmail = cookie;
      deleteCookie(cookieName);
    } else if (window && window.location.search) {
      const parsed = queryString.parse(window.location.search);
      if (parsed.email) {
        try {
          parsedEmail = atob(parsed.email);
        } catch {
          // Ignore the error caused by atob.
          return;
        }
        setFieldValueByName('Email')(parsedEmail);
      }
    }

    if (parsedEmail) {
      // We hide loading if we got validation error, otherwise we try to save customer.
      setLoading(true);
      validationSchema
        .validate({
          Email: parsedEmail,
        })
        .then(() => {
          return onSubmit(parsedEmail);
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, []);

  if (isLoading) {
    return <LoadingPlaceholder />;
  }

  const submitButtonText = getSubmitButton(enabledMsStartupSignup, enabledStartupConferenceSignup);
  const socialSignupStyle = !isMsStartupOrStartupConference && styles.isSocialSignupEnabled;
  const msStartupSignupStyle = isMsStartupOrStartupConference && styles.isMsStartupSignupEnabled;

  return (
    <SignUpLayout showLogo={false} includeLogInLink>
      <div className={cx(styles.container, socialSignupStyle, msStartupSignupStyle)}>
        <div className={styles.logoContainer}>
          <a href="/">
            <img src={logo} alt="Ansarada" width="32" height="32" />
          </a>
        </div>
        <h1 className={cx(styles.title, socialSignupStyle)}>
          {isMsStartupOrStartupConference
            ? 'Supercharge your capital raise with Ansarada'
            : "Let's turn your opportunity into a winning outcome"}
        </h1>
        {isMsStartupOrStartupConference && (
          <p className={styles.desc}>We suggest using your work email address</p>
        )}
        <FormGridCell
          labelText=""
          errors={touched.Email && errors.Email}
          errorPosition="Bottom"
          hint={touched.Email && errors.Email ? undefined : EMPTY_HINT}
          hintPosition="Bottom"
        >
          <TextInput
            required
            inputRef={firstInputRef}
            maxLength={70}
            type="Email"
            placeholder="Enter your email address"
            status={fieldStatusByName('Email')}
            value={Email}
            className={styles.emailInput}
            onChangeValue={setFieldValueByName('Email')}
            onBlur={setFieldTouchedByName('Email')}
            id="s1-email"
          />
        </FormGridCell>
        <div className={cx(styles.ctaContainer, socialSignupStyle)}>
          <Button
            // $FlowFixMe
            buttonRef={submitButtonRef}
            size="Medium"
            variant="Primary"
            onClick={handleSubmit}
            loading={isSubmitting}
            className={styles.submitBtn}
            id="s1-next"
          >
            {submitButtonText}
          </Button>
        </div>
        {!isMsStartupOrStartupConference && (
          <>
            <FormGridCell labelText="" hintPosition="Bottom" hint={EMPTY_HINT}>
              <p className={styles.divider}>or sign up with</p>
            </FormGridCell>
            <FormGridCell labelText="" hintPosition="Bottom" hint={EMPTY_HINT}>
              <div className={cx(styles.socialButtonContainer, socialSignupStyle)}>
                <Link
                  href={getSocialSignupLink('google', window.location.href.split('/').pop())}
                  className={styles.googleButton}
                  variant="GhostInverse"
                  size="Small"
                  analyticsEvent={{
                    event: 'f_website_google-social.clicked',
                  }}
                >
                  <img
                    src={googleLogo}
                    alt="Google"
                    width="16"
                    height="16"
                    className={styles.socialLogo}
                  />
                  <span>Google</span>
                </Link>
                <Link
                  href={getSocialSignupLink('linkedin', window.location.href.split('/').pop())}
                  className={styles.linkedinButton}
                  variant="GhostInverse"
                  size="Small"
                  analyticsEvent={{
                    event: 'f_website_linkedin-social.clicked',
                  }}
                >
                  <img
                    src={linkedinLogo}
                    alt="LinkedIn"
                    width="16"
                    height="16"
                    className={styles.socialLogo}
                  />
                  <span>LinkedIn</span>
                </Link>
                <Link
                  href={getSocialSignupLink('microsoft', window.location.href.split('/').pop())}
                  className={styles.microsoftButton}
                  variant="GhostInverse"
                  size="Small"
                  analyticsEvent={{
                    event: 'f_website_microsoft-social.clicked',
                  }}
                >
                  <img
                    src={microsoftLogo}
                    alt="Microsoft"
                    width="16"
                    height="16"
                    className={styles.socialLogo}
                  />
                  <span>Microsoft</span>
                </Link>
              </div>
            </FormGridCell>
          </>
        )}
        <p className={styles.secondaryText}>No credit card required</p>
        {recaptchaFailed && (
          <Message variant="criticalerror" context="page">
            {/* eslint-disable-next-line react/no-unescaped-entities */}
            That didn't work. Press '{submitButtonText}' again.
          </Message>
        )}
      </div>
    </SignUpLayout>
  );
};

export default SignUpFormV2;
